import { DAY_RATES_MAX_MIN_DAYS, DAY_RATES_MAX_PRICE_CENTS } from '@rental/domain'
import { parseEuroCents } from '../money'

/** ISO weekdays, Monday 1 to Sunday 7. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7
export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 7]
export const WEEKEND: readonly Weekday[] = [6, 7]

export type PriceMode = 'leave' | 'to' | 'plus' | 'minus'
export type MinMode = 'leave' | 'clear' | 'value'
export type StopMode = 'leave' | 'on' | 'off'

/** What the bulk dialog holds, as typed. */
export interface BulkForm {
  categoryIds: readonly string[]
  from: string
  to: string
  weekdays: readonly Weekday[]
  priceMode: PriceMode
  amount: string
  minPickupMode: MinMode
  minPickup: string
  minThroughMode: MinMode
  minThrough: string
  stopMode: StopMode
}

export type PriceChange = { to: number } | { plusCents: number } | { minusCents: number }

/** What is sent to preview and to apply (StaffPricesBulkRequest). */
export interface BulkRequest {
  categoryIds: string[]
  from: string
  to: string
  weekdays: Weekday[]
  set: {
    price?: PriceChange
    minDaysPickup?: number | null
    minDaysThrough?: number | null
    stopSale?: boolean
  }
}

/** Why the preview cannot be asked yet, each a key of `prices.why`. */
export type BulkReason = 'categories' | 'weekdays' | 'range' | 'past' | 'nothing' | 'amount' | 'min'

/** A price typed for a day or a bulk change: cents, 1 to the most a day may cost; null when not one. */
export function priceCentsOf(text: string): number | null {
  const cents = parseEuroCents(text)
  return cents !== null && cents >= 1 && cents <= DAY_RATES_MAX_PRICE_CENTS ? cents : null
}

/** Minimum days typed: a whole number, 1 to DAY_RATES_MAX_MIN_DAYS; null when not one. */
export function minDaysOf(text: string): number | null {
  const trimmed = text.trim()
  if (!/^\d{1,3}$/.test(trimmed)) return null
  const value = Number(trimmed)
  return value >= 1 && value <= DAY_RATES_MAX_MIN_DAYS ? value : null
}

/**
 * The request a bulk form makes, or why it cannot be made yet. A field left
 * as it is stays out of `set`; "clear" sends null. The server checks it all
 * again: this only keeps the Owner from asking for a preview that cannot be.
 */
export function bulkRequest(
  form: BulkForm,
  today: string,
): { ok: true; body: BulkRequest } | { ok: false; reasons: BulkReason[] } {
  const reasons: BulkReason[] = []
  if (form.categoryIds.length === 0) reasons.push('categories')
  if (form.weekdays.length === 0) reasons.push('weekdays')
  if (form.to < form.from) reasons.push('range')
  if (form.from < today) reasons.push('past')
  const set: BulkRequest['set'] = {}
  if (form.priceMode !== 'leave') {
    const cents = priceCentsOf(form.amount)
    if (cents === null) reasons.push('amount')
    else
      set.price =
        form.priceMode === 'to'
          ? { to: cents }
          : form.priceMode === 'plus'
            ? { plusCents: cents }
            : { minusCents: cents }
  }
  const minimum = (mode: MinMode, text: string): number | null | undefined => {
    if (mode === 'leave') return undefined
    if (mode === 'clear') return null
    const days = minDaysOf(text)
    if (days === null && !reasons.includes('min')) reasons.push('min')
    return days ?? undefined
  }
  const pickup = minimum(form.minPickupMode, form.minPickup)
  if (pickup !== undefined) set.minDaysPickup = pickup
  const through = minimum(form.minThroughMode, form.minThrough)
  if (through !== undefined) set.minDaysThrough = through
  if (form.stopMode !== 'leave') set.stopSale = form.stopMode === 'on'
  const nothing =
    form.priceMode === 'leave' &&
    form.minPickupMode === 'leave' &&
    form.minThroughMode === 'leave' &&
    form.stopMode === 'leave'
  if (nothing) reasons.push('nothing')
  if (reasons.length > 0) return { ok: false, reasons }
  return {
    ok: true,
    body: {
      categoryIds: [...form.categoryIds],
      from: form.from,
      to: form.to,
      weekdays: WEEKDAYS.filter((day) => form.weekdays.includes(day)),
      set,
    },
  }
}
